import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import './LoginPage.css';
import { useNavigate } from 'react-router-dom';
import { setUserDetails, setUserId, setUserName } from '../../../redux/userSlice';
import { useDispatch } from 'react-redux';
import { FaUser, FaEnvelope, FaLock, FaCheckCircle, FaArrowRight, FaShieldAlt, FaClock, FaGlobeAmericas } from 'react-icons/fa';
import { apiUrl } from '../../../config/api.js';
import { devLog } from '../../../config/devLog.js';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [stats, setStats] = useState({ users: 0, listings: 0, connections: 0 });
  const [rememberMe, setRememberMe] = useState(localStorage.getItem('rememberMe') === 'true');
  const dispatch = useDispatch()
  
  const navigate = useNavigate()

  // Animated counter effect
  useEffect(() => {
    const animateCounters = () => {
      const targetStats = { users: 500, listings: 200, connections: 50 };
      const duration = 500;
      const steps = 60;
      const stepDuration = duration / steps;
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        setStats({
          users: Math.floor(targetStats.users * progress),
          listings: Math.floor(targetStats.listings * progress),
          connections: Math.floor(targetStats.connections * progress)
        });
        
        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    }

    const timer = setTimeout(animateCounters, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tryAutoLogin = async () => {
      try {
        if (localStorage.getItem('rememberMe') === 'true') {
          const storedUserId = localStorage.getItem('userID');
          if (storedUserId) {
            const res = await fetch(apiUrl("getUser?" + storedUserId));
            if (res.ok) {
              const profileData = await res.json();
              dispatch(setUserId(storedUserId));
              dispatch(setUserName(profileData.first_name + ' ' + profileData.last_name));
              dispatch(setUserDetails({ ...profileData, userId: profileData.id, name: profileData.first_name + " " + profileData.last_name, userEmail: profileData.email, company: profileData.companyName ? profileData.companyName : "" }));
              // if (profileData.token) localStorage.setItem('token', profileData.token);
              navigate('/home');
            }
          }
        }
      } catch (err) {
        devLog('Auto-login failed', err?.message);
      }
    };
    tryAutoLogin();
  }, [dispatch, navigate]);

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Software Engineer, Dallas",
      avatar: "RK",
      quote: "Great platform to connect with fellow NRIs! Found helpful advice about rental properties in India from others who've been there.",
      rating: 5
    },
    {
      name: "Priya Sharma",
      role: "Doctor, California",
      avatar: "PS",
      quote: "Love connecting with other NRIs for visa guidance. It's helpful to get insights from those who've gone through similar processes.",
      rating: 5
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // navigate("/home")
    if (!validateForm()) return;
    handleLogin();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      devLog('[login] submit finished', { email: formData.email });
    }, 500);
  };


  const handleLogin = async () => {
    // if (!validateInputs()) return;

    try {
      const response = await fetch(apiUrl("login"), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email:formData.email, password:formData.password }),
      });

      const data = await response.json();
      devLog('[login]', { ok: response.ok, userId: data?.userId });
      if (response.ok) {
        setApiError('');
        dispatch(setUserId(data.userId))
        dispatch(setUserName(data.name))
        const profileRes = await fetch(apiUrl("getUser?" + data.userId))
        const profileData = await profileRes.json()
         if (data.token) localStorage.setItem('token', data.token);
          localStorage.setItem('userID', data.userId);
        // Persist credentials only when user opted for "Remember me"
        if (rememberMe) {
         
          localStorage.setItem('rememberMe', 'true');
        } 
        dispatch(setUserDetails({ ...profileData, userId: profileData.id, name: profileData.first_name + " " + profileData.last_name, userEmail: profileData.email, company: profileData.companyName ? profileData.companyName : "" }))

        // Navigate to another screen on successful login
        navigate('/home');
      } else {
        setApiError(data.message || 'Invalid credentials.');
      }
    } catch (error) {
      setApiError('Something went wrong. Please try again.');
    }
  };

  const handleRememberChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    localStorage.setItem('rememberMe', checked ? 'true' : 'false');
  };

  return (
    <div className="login-container">
      
      <div className="login-layout">
        {/* Left Side - Trust Building */}
        <div className="login-left">
          <div className="trust-content">
            <div className="trust-header">
              <div className="trust-icon">
                <FaUser className="icon" />
              </div>
              <h1>Welcome Back to Corporate NRI</h1>
              <p>Sign in to access your community dashboard and connect with fellow USA NRIs</p>
            </div>

            <div className="trust-stats">
              <div className="stat-item">
                <div className="stat-number">{stats.users.toLocaleString()}+</div>
                <div className="stat-label">Active USA NRIs</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.listings.toLocaleString()}+</div>
                <div className="stat-label">Verified Listings</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{stats.connections.toLocaleString()}+</div>
                <div className="stat-label">Connections Made</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Free Forever</div>
              </div>
            </div>

            <div className="trust-features">
              <div className="feature-item">
                <FaCheckCircle className="feature-icon" />
                <span>Verified USA NRI Community</span>
              </div>
              <div className="feature-item">
                <FaShieldAlt className="feature-icon" />
                <span>Safe & Secure Platform</span>
              </div>
              <div className="feature-item">
                <FaClock className="feature-icon" />
                <span>24/7 Community Support</span>
              </div>
              <div className="feature-item">
                <FaGlobeAmericas className="feature-icon" />
                <span>No Hidden Fees</span>
              </div>
            </div>

            <div className="trust-testimonial">
              <div className="testimonial-content">
                <p>"{testimonials[currentTestimonial].quote}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonials[currentTestimonial].avatar}</div>
                  <div className="author-info">
                    <strong>{testimonials[currentTestimonial].name}</strong>
                    <span>{testimonials[currentTestimonial].role}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-right">
          <div className="login-form-wrapper">
            <div className="form-header">
              <h2>Welcome Back!</h2>
              <p>Sign in to your account to access the community</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <label className="input-label">
                  <FaEnvelope className="input-icon" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? 'input-error' : ''}`}
                  placeholder="Enter your email address"
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>

              <div className="input-group">
                <label className="input-label">
                  <FaLock className="input-icon" />
                  Password
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`form-input ${errors.password ? 'input-error' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <div className="error-message">{errors.password}</div>}
                {apiError && <div className="error-message">{apiError}</div>}
              </div>

              <div className="form-options">
                <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input type="checkbox" className="checkbox-input" checked={rememberMe} onChange={handleRememberChange} />
                        <span className="checkbox-custom"></span>
                        Remember me
                      </label>
                </div>
                <button type="button" className="forgot-password-btn" onClick={() => navigate("/forgotPassword")}>
                  Forgot password?
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={isLoading} className="submit-button">
                  {isLoading ? (
                    <div className="loading-spinner"></div>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <FaArrowRight className="btn-icon" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="signup-link-section">
              <p className="signup-text">New to Corporate NRI?</p>
              <button 
                className="signup-link-btn"
                onClick={() => navigate("/signUp")}
              >
                Create your account and join our community →
              </button>
            </div>

            <div className="login-guarantee">
              <span className="guarantee-icon">🆓</span>
              <span>Completely Free • No Hidden Fees • Join Anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
